import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { i18n } from '@lingui/core';
import { useSelector } from 'react-redux';

/**
 * Вычисляет яркость (luminance) цвета в диапазоне 0-1
 * Используется для определения контрастного цвета текста
 */
const getLuminance = colorValue => {
    const colorMap = {
        'white': '#ffffff',
        'yellow': '#ffff00',
        'black': '#000000',
        'red': '#ff0000',
        'green': '#008000',
        'blue': '#0000ff',
    };

    let hexColor = colorValue.toLowerCase();
    if (colorMap[hexColor]) {
        hexColor = colorMap[hexColor];
    }

    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
    return luminance;
};
const getContrastColor = hexColor => {
    const luminance = getLuminance(hexColor);
    return luminance > 0.45 ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.95)';
};
const getVisibleIndices = (itemCount, containerWidth) => {
    if (itemCount <= 2) return Array.from({ length: itemCount }, (_, i) => i);

    let step = 1;
    let thresholdNarrow;
    let thresholdVeryNarrow;
    let thresholdExtremelyNarrow;

    if (itemCount <= 5) {
        thresholdNarrow = 180;
        thresholdVeryNarrow = 120;
        thresholdExtremelyNarrow = 0;
    } else if (itemCount <= 8) {
        thresholdNarrow = 260;
        thresholdVeryNarrow = 150;
        thresholdExtremelyNarrow = 0;
    } else if (itemCount <= 12) {
        thresholdNarrow = 320;
        thresholdVeryNarrow = 200;
        thresholdExtremelyNarrow = 0;
    } else {
        thresholdNarrow = 420;
        thresholdVeryNarrow = 300;
        thresholdExtremelyNarrow = 200;
    }

    if (containerWidth < thresholdExtremelyNarrow) {
        step = 4;
    } else if (containerWidth < thresholdVeryNarrow) {
        step = 3;
    } else if (containerWidth < thresholdNarrow) {
        step = 2;
    }

    const visibleIndices = [ 0 ];

    for (let i = step; i < itemCount - 1; i += step) {
        visibleIndices.push(i);
    }

    if (visibleIndices[visibleIndices.length - 1] !== itemCount - 1) {
        visibleIndices.push(itemCount - 1);
    }

    return visibleIndices;
};

/**
 * DynamicGradientLegend - Градиентная легенда с динамическими подписями
 *
 * Используется для H3 слоя, где подписи должны меняться в зависимости от h3_zoom
 * Цвета градиента остаются неизменными, меняются только числовые значения подписей
 *
 * Props:
 * - items: базовые элементы (с цветами)
 * - h3ZoomReduxKey: ключ в Redux для получения текущего zoom (напр. 'aggTracksAugustH3Zoom')
 * - baseZoom: базовый zoom уровень, для которого заданы значения (обычно 10)
 * - scalingFactor: множитель для масштабирования (обычно 3 для H3, изменён с 7)
 * - minZoom: минимальный zoom (значения не масштабируются ниже него)
 * - maxZoom: максимальный zoom (значения не масштабируются выше него)
 * - baseValues: исходные значения для подписей (массив чисел)
 * - renderConfig: конфигурация рендеринга (gradientHeight, description)
 */
export const DynamicGradientLegend = ({
    items,
    h3ZoomReduxKey,
    baseZoom = 10,
    scalingFactor = 3,
    minZoom = 8,
    maxZoom = 12,
    baseValues = [],
    renderConfig = {},
}) => {
    const {
        gradientHeight = '20px',
        description = null,
        // noAutoScale: true — отключает авто-конвертацию × 1000 (для baseValues в сырых числах, а не тысячах)
        noAutoScale = false,
    } = renderConfig;
    const reduxZoom = useSelector(state => {
        const parts = h3ZoomReduxKey.split('.');
        let value = state;
        for (const part of parts) {
            value = value[part];
            if (!value) return null;
        }
        return value;
    });
    const [ isMobile, setIsMobile ] = useState(window.innerWidth < 850);
    const [ containerWidth, setContainerWidth ] = useState(0);
    const [ currentMapZoom, setCurrentMapZoom ] = useState(baseZoom);
    const containerRef = useRef(null);
    const gradientRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 850);
            if (gradientRef.current) {
                setContainerWidth(gradientRef.current.offsetWidth);
            }
        };
        window.addEventListener('resize', handleResize);
        setTimeout(() => {
            if (gradientRef.current) {
                setContainerWidth(gradientRef.current.offsetWidth);
            }
        }, 0);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Слушаем изменения zoom карты для режима 'auto'
    useEffect(() => {
        if (reduxZoom !== 'auto') {
            return;
        }
        // eslint-disable-next-line no-underscore-dangle
        const map = window.__map;
        if (!map) {
            return;
        }
        const handleZoomChange = () => {
            // Используем текущий zoom без округления для более быстрой реакции
            const rawZoom = map.getZoom();
            // Ограничиваем диапазоном и округляем для H3
            const boundedZoom = Math.max(minZoom, Math.min(maxZoom, Math.round(rawZoom)));
            setCurrentMapZoom(boundedZoom);
        };
        // Используем 'zoomend' вместо 'zoom' для более стабильного обновления
        map.on('zoom', handleZoomChange);
        map.on('sourcedata', handleZoomChange); // Обновляем при загрузке новых тайлов
        // Инициализируем текущим zoom при монтировании
        handleZoomChange();
        return () => {
            map.off('zoom', handleZoomChange);
            map.off('sourcedata', handleZoomChange);
        };
    }, [ reduxZoom, minZoom, maxZoom ]);
    const getEffectiveZoom = () => {
        if (reduxZoom === 'auto') {
            // В режиме 'auto' используем текущий zoom карты, ограниченный диапазоном [minZoom, maxZoom]
            return Math.max(minZoom, Math.min(maxZoom, currentMapZoom));
        }
        const numZoom = parseInt(reduxZoom, 10);
        return Math.max(minZoom, Math.min(maxZoom, numZoom));
    };
    const getScalingCoefficient = zoom => {
        // Инвертированная формула для легенды:
        // zoom > baseZoom (мелкие ячейки) → коэффициент < 1 (значения уменьшаются)
        // zoom < baseZoom (крупные ячейки) → коэффициент > 1 (значения увеличиваются)
        const zoomDiff = baseZoom - zoom;
        return Math.pow(scalingFactor, zoomDiff);
    };
    const scaleValue = (baseValue, zoom) => {
        const coefficient = getScalingCoefficient(zoom);
        return baseValue * coefficient;
    };
    const effectiveZoom = getEffectiveZoom();
    const scaledValues = items.map((item, idx) => {
        const baseValue = baseValues[idx] || 0;
        return scaleValue(baseValue, effectiveZoom);
    });
    // Определяем, нужно ли переключиться на единицы (вместо тысяч)
    // Порог: если минимальное значение < 1 тыс., показываем в единицах
    // noAutoScale=true отключает эту логику (для baseValues в сырых числах, а не тысячах)
    const minScaledValue = Math.min(...scaledValues.filter(v => v > 0));
    const shouldUseUnits = !noAutoScale && minScaledValue < 1;
    // Конвертируем значения если нужно (тысячи → единицы)
    const displayValues = shouldUseUnits
        ? scaledValues.map(v => v * 1000)
        : scaledValues;
    const formatValue = (value, decimals = 0) => {
        const roundedValue = Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
        const formatted = decimals === 0 ? String(Math.round(roundedValue)) : roundedValue.toFixed(decimals);
        if (roundedValue >= 1000000) {
            return `${(roundedValue / 1000000).toFixed(decimals)}M`;
        }
        if (roundedValue >= 1000) {
            return `${(roundedValue / 1000).toFixed(decimals)}K`;
        }
        return formatted;
    };

    // Определяем оптимальную точность округления
    let decimals = 0;
    let uniqueCount = new Set(displayValues.map(v => formatValue(v, decimals))).size;

    if (uniqueCount < 3) {
        decimals = 1;
        uniqueCount = new Set(displayValues.map(v => formatValue(v, decimals))).size;
    }

    if (uniqueCount < 3) {
        decimals = 2;
    }

    const scaledItems = items.map((item, idx) => {
        const displayValue = displayValues[idx];
        const formattedValue = formatValue(displayValue, decimals);
        return {
            ...item,
            label: formattedValue,
            scaledValue: formattedValue, // Сохраняем форматированное значение для фильтрации
            rawValue: displayValue,
        };
    });
    // Фильтруем дубликаты: показываем только те элементы, где значение уникально
    const uniqueValues = new Set();
    const filteredItems = scaledItems.filter((item, idx) => {
        // Всегда показываем первый и последний
        if (idx === 0 || idx === scaledItems.length - 1) {
            uniqueValues.add(item.scaledValue);
            return true;
        }
        // Для остальных показываем только если значение еще не встречалось
        if (!uniqueValues.has(item.scaledValue)) {
            uniqueValues.add(item.scaledValue);
            return true;
        }
        return false;
    });
    // Маршрутизируем индексы исходных элементов для фильтрованного списка
    const filteredIndices = scaledItems.reduce((acc, item, idx) => {
        if (filteredItems.includes(item)) {
            acc.push(idx);
        }
        return acc;
    }, []);
    const colors = items.map(item => item.color || item.fill);
    const gradientString = `linear-gradient(90deg, ${colors.join(', ')})`;
    const height = isMobile ? '20px' : gradientHeight;
    const visibleIndices = containerWidth > 0
        ? getVisibleIndices(filteredIndices.length, containerWidth)
        : Array.from({ length: filteredIndices.length }, (_, i) => i);

    return (
        <div
            style={{ marginLeft: '10px', marginRight: '10px', marginBottom: '4px' }}
            ref={containerRef}
        >
            {/* Градиент с подписями */}
            <div
                style={{
                    width: '100%',
                    height,
                    background: gradientString,
                    opacity: 0.85,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingLeft: '2px',
                    paddingRight: '2px',
                    paddingTop: '2px',
                    paddingBottom: '2px',
                    boxSizing: 'border-box',
                    position: 'relative',
                    flexWrap: 'nowrap',
                    overflow: 'hidden',
                    marginBottom: description ? '0px' : '0px',
                }}
                ref={gradientRef}
            >
                {filteredItems.map((item, filteredIdx) => {
                    const textColor = getContrastColor(item.color || item.fill);
                    const isVisible = visibleIndices.includes(filteredIdx);
                    const isFirst = filteredIdx === 0;
                    const isLast = filteredIdx === filteredItems.length - 1;
                    const paddingLeftValue = isFirst ? '6px' : '1px';
                    const paddingRightValue = isLast ? '6px' : '1px';

                    return (
                        <span
                            key={item.id || filteredIdx}
                            style={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap',
                                color: textColor,
                                fontWeight: '500',
                                flex: 1,
                                minWidth: 0,
                                textAlign: 'center',
                                pointerEvents: 'none',
                                display: isVisible ? 'flex' : 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                paddingLeft: paddingLeftValue,
                                paddingRight: paddingRightValue,
                                paddingTop: '0px',
                                paddingBottom: '0px',
                                margin: '0px',
                                lineHeight: '1.2',
                            }}
                        >
                            {typeof item.label === 'string' ? item.label : i18n._(item.label)}
                        </span>
                    );
                })}
            </div>

            {/* Опциональное пояснение */}
            {description && (
                <div
                    style={{
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.7)',
                        marginLeft: '10px',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        paddingLeft: '0px',
                        paddingRight: '2px',
                    }}
                >
                    {typeof description === 'string' ? description : i18n._(description)}
                </div>
            )}
        </div>
    );
};

DynamicGradientLegend.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        color: PropTypes.string,
        fill: PropTypes.string,
    })).isRequired,
    h3ZoomReduxKey: PropTypes.string.isRequired,
    baseZoom: PropTypes.number,
    scalingFactor: PropTypes.number,
    minZoom: PropTypes.number,
    maxZoom: PropTypes.number,
    baseValues: PropTypes.arrayOf(PropTypes.number).isRequired,
    renderConfig: PropTypes.shape({
        gradientHeight: PropTypes.string,
        description: PropTypes.oneOfType([ PropTypes.string, PropTypes.object ]),
    }),
};

export default DynamicGradientLegend;
